import React, {useState, useEffect, useMemo} from 'react';
import {View, Pressable, Platform} from 'react-native';
import {Calendar} from 'react-native-calendars';
import moment from 'moment';

import Divider from '../Divider';
import Text from '../Text';
import {DAYS_OF_WEEK, FONTS} from '../../../utils/Constants';
import {useTheme, useThemedStyles} from '../../../theme';
import makeStyles from './styles';
import {tID} from '../../../utils/TestIdHelper';
import {LeftArrowIcon, RightArrowIcon} from '../../../utils/SvgIcons';
import CommonStyles from '../../../utils/CommonStyles';

// Marked-date descriptors reference themed styles, so they are built per
// theme (from the makeStyles output) instead of at module load.
const makeDayMarks = styles => ({
  selectedDayMark: {
    selected: true,
    startingDay: true,
    endingDay: true,
    customStyles: {
      container: styles.selectedDateContainer,
      text: styles.selectedDayText,
    },
  },
  singleDayMark: {
    selected: true,
    startingDay: true,
    endingDay: true,
    customStyles: {
      container: styles.selectedDateContainer,
      text: styles.singleDayText,
    },
  },
  middleDayMark: {
    selected: true,
    customStyles: {
      container: styles.middleDayContainer,
      text: styles.middleDayText,
    },
  },
  startDayMark: {
    selected: true,
    startingDay: true,
    customStyles: {
      container: styles.startDateContainer,
      text: styles.selectedDayText,
    },
  },
  endDayMark: {
    selected: true,
    endingDay: true,
    customStyles: {
      container: styles.endDateContainer,
      text: styles.selectedDayText,
    },
  },
});

const makeCalendarTheme = colors => ({
  calendarBackground: colors.surface,
  selectedDayBackgroundColor: colors.accentStrong,
  selectedDayTextColor: colors.textOnAccent,
  todayTextColor: colors.accentStrong,
  dayTextColor: colors.textSecondary,
  textDisabledColor: colors.textDisabled,
  disabledArrowColor: colors.borderMuted,
  indicatorColor: colors.link,
  textDayFontFamily: FONTS.regularFont,
  textDayFontWeight: '600',
  textDayFontSize: 14,
});

const CalendarView = ({
  onSelectDate,
  selectedDate,
  disabled,
  isSingleSelect = false,
  disable,
}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  const {
    selectedDayMark,
    singleDayMark,
    middleDayMark,
    startDayMark,
    endDayMark,
  } = useMemo(() => makeDayMarks(styles), [styles]);
  const calendarTheme = useMemo(() => makeCalendarTheme(colors), [colors]);

  const today = moment().format('YYYY-MM-DD');
  const [currentDate, setCurrentDate] = useState({
    [selectedDate ? selectedDate : today]: isSingleSelect
      ? singleDayMark
      : selectedDayMark,
  });

  useEffect(() => {
    if (disabled) {
      setCurrentDate({});
      if (onSelectDate) {
        onSelectDate(today, today);
      }
    } else {
      setCurrentDate({
        [today]: selectedDayMark,
        [selectedDate ? selectedDate : today]: isSingleSelect
          ? singleDayMark
          : selectedDayMark,
      });
      if (onSelectDate) {
        onSelectDate(today, today);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset selection only when disabled toggles; re-firing on onSelectDate/selectedDate would clobber the user's selection
  }, [disabled]);

  const onPressDay = day => {
    const objectLength = Object.keys(currentDate);
    if (isSingleSelect) {
      if (today == day.dateString) {
        setCurrentDate({[day.dateString]: singleDayMark});
      } else {
        setCurrentDate({
          [day.dateString]: singleDayMark,
          [today]: selectedDayMark,
        });
      }
      if (onSelectDate) {
        onSelectDate(day.dateString);
      }
    } else {
      if (objectLength.length == 0) {
        setCurrentDate({[day.dateString]: selectedDayMark});
        if (onSelectDate) {
          onSelectDate(day.dateString, day.dateString);
        }
      } else if (objectLength.length == 1) {
        if (moment(objectLength[0]).isBefore(day.dateString)) {
          let dates = {};

          let currDate = moment(objectLength[0]).startOf('day');
          let lastDate = moment(day.dateString).startOf('day');

          while (currDate.add(1, 'days').diff(lastDate) < 0) {
            dates[currDate.format('YYYY-MM-DD')] = middleDayMark;
          }

          setCurrentDate({
            [objectLength[0]]: startDayMark,
            ...dates,
            [day.dateString]: endDayMark,
          });
          if (onSelectDate) {
            onSelectDate(objectLength[0], day.dateString);
          }
        } else {
          var dates = {};

          var currDate = moment(day.dateString).startOf('day');
          var lastDate = moment(objectLength[0]).startOf('day');

          while (currDate.add(1, 'days').diff(lastDate) < 0) {
            dates[currDate.format('YYYY-MM-DD')] = middleDayMark;
          }

          setCurrentDate({
            [day.dateString]: startDayMark,
            ...dates,
            [objectLength[0]]: endDayMark,
          });
          if (onSelectDate) {
            onSelectDate(day.dateString, objectLength[0]);
          }
        }
      } else if (objectLength.length >= 2) {
        setCurrentDate({[day.dateString]: selectedDayMark});
        if (onSelectDate) {
          onSelectDate(day.dateString, day.dateString);
        }
      }
    }
  };

  const CustomHeader = React.forwardRef((props, ref) => {
    return (
      <View testID={tID('CalendarView-View-CustomHeader')} ref={ref} {...props}>
        <View style={styles.headerContainer}>
          <Text
            testID={tID('CalendarView-Text-MonthYear')}
            variation="Body1SemiBold"
            color={colors.textPrimary}
          >
            {moment(props.month.toUTCString()).format('MMM YYYY').toUpperCase()}
          </Text>
          <View style={styles.buttonContainer}>
            <Pressable
              testID={tID('CalendarView-Pressable-LeftArrow')}
              hitSlop={styles.buttonHitSlop}
              onPress={() => props.addMonth(-1)}
            >
              <LeftArrowIcon testID={tID('CalendarView-Image-LeftArrow')} />
            </Pressable>
            <View style={CommonStyles.mh_12} />
            <Pressable
              testID={tID('CalendarView-Pressable-RightArrow')}
              hitSlop={styles.buttonHitSlop}
              onPress={() => props.addMonth(1)}
            >
              <RightArrowIcon testID={tID('CalendarView-Image-RightArrow')} />
            </Pressable>
          </View>
        </View>
        <Divider top={16} />
        <View style={styles.dayNameContainer}>
          {DAYS_OF_WEEK.map((day, index) => (
            <Text
              testID={tID('CalendarView-Text-Day')}
              key={index}
              variation="Text2Medium"
              textAlign="center"
              color={colors.link}
              style={styles.setFlex}
            >
              {day}
            </Text>
          ))}
        </View>
      </View>
    );
  });

  return (
    <Calendar
      testID={tID('CalendarView-Calendar-Main')}
      disabledByDefault={disabled}
      markingType={'custom'}
      theme={calendarTheme}
      markedDates={currentDate}
      maxDate={today}
      onDayPress={onPressDay}
      // onDayLongPress={day => {
      //   console.log('selected day', day);
      // }}
      // onMonthChange={month => {
      //   console.log('month changed', month);
      // }}
      customHeader={CustomHeader}
      firstDay={1}
      disableAllTouchEventsForDisabledDays={false}
    />
  );
};

export default CalendarView;
